import { lazy, Suspense } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import "@/styles/global.css";

// Import critical sections eagerly
import PricingHero from "@/components/sections/PricingHero";
import PricingTiers from "@/components/sections/PricingTiers";

// Lazy load non-critical sections
const FAQSection = lazy(() => import("@/components/sections/FAQSection"));

// Invisible loading component that doesn't disrupt the layout
const SectionLoader = () => <div className="min-h-[1px]"></div>;

const Pricing = () => {
  // Enhanced structured data for pricing page
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      // Product with pricing offers
      {
        "@type": "Product",
        "@id": "https://coderide.ai/pricing#product",
        "name": "CodeRide",
        "description": "Task Management Redesigned for AI. Choose the perfect plan for your AI coding needs.",
        "brand": {
          "@type": "Brand",
          "name": "CodeRide"
        },
        "category": "Developer Tools",
        "image": "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/coderide_dashboard-2.webp?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NvZGVyaWRlX2Rhc2hib2FyZC0yLndlYnAiLCJpYXQiOjE3NDUwNjExNTIsImV4cCI6MTc3NjU5NzE1Mn0.fiQCDIjdWtByoU3vFYwTmoFTqCQ7jXN74FMr291bKdY",
        "offers": [
          {
            "@type": "Offer",
            "name": "Explorer Plan",
            "price": "0",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "url": "https://coderide.ai/pricing",
            "description": "Perfect for exploring CodeRide and launch your first AI-optimized project. Includes 1 project and 40 AI-Enhanced Tasks (una tantum allocation).",
            "eligibleQuantity": {
              "@type": "QuantitativeValue",
              "value": "40",
              "unitText": "AI-Enhanced Tasks"
            },
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "0",
              "priceCurrency": "EUR",
              "valueAddedTaxIncluded": true
            },
            "itemOffered": {
              "@type": "Service",
              "name": "CodeRide Explorer",
              "serviceType": "AI Development Tools",
              "category": "Software Development"
            }
          },
          {
            "@type": "Offer",
            "name": "Creator Plan",
            "price": "9",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "url": "https://coderide.ai/pricing",
            "description": "Scale your AI coding across unlimited projects and zero context window problems. Includes 100 AI-Enhanced Tasks per month.",
            "eligibleQuantity": {
              "@type": "QuantitativeValue",
              "value": "100",
              "unitText": "AI-Enhanced Tasks per month"
            },
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "9",
              "priceCurrency": "EUR",
              "billingIncrement": "P1M",
              "valueAddedTaxIncluded": true
            },
            "itemOffered": {
              "@type": "Service",
              "name": "CodeRide Creator",
              "serviceType": "AI Development Tools",
              "category": "Software Development"
            }
          },
          {
            "@type": "Offer",
            "name": "Pro Plan",
            "price": "29",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "url": "https://coderide.ai/pricing",
            "description": "Professional-grade AI coding with unlimited projects and 400 AI-Enhanced Tasks per month for developing complex products.",
            "eligibleQuantity": {
              "@type": "QuantitativeValue",
              "value": "400",
              "unitText": "AI-Enhanced Tasks per month"
            },
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "29",
              "priceCurrency": "EUR",
              "billingIncrement": "P1M",
              "valueAddedTaxIncluded": true
            },
            "itemOffered": {
              "@type": "Service",
              "name": "CodeRide Pro",
              "serviceType": "AI Development Tools",
              "category": "Software Development"
            }
          },
          {
            "@type": "Offer",
            "name": "Early Adopter Plan",
            "price": "119",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/LimitedAvailability",
            "url": "https://coderide.ai/pricing",
            "description": "Lifetime access to all CodeRide features with unlimited projects and 2000 AI-Enhanced Tasks (una tantum allocation). Limited to 200 seats.",
            "eligibleQuantity": {
              "@type": "QuantitativeValue",
              "value": "2000",
              "unitText": "AI-Enhanced Tasks"
            },
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "119",
              "priceCurrency": "EUR",
              "valueAddedTaxIncluded": true
            },
            "inventoryLevel": {
              "@type": "QuantitativeValue",
              "value": "200",
              "unitText": "seats available"
            },
            "itemOffered": {
              "@type": "Service",
              "name": "CodeRide Early Adopter",
              "serviceType": "AI Development Tools",
              "category": "Software Development"
            }
          }
        ]
      },
      // Organization
      {
        "@type": "Organization",
        "@id": "https://coderide.ai/#organization",
        "name": "CodeRide",
        "url": "https://coderide.ai",
        "description": "CodeRide eliminates the context reset cycle in AI coding, ensuring your assistant retains complete project understanding between sessions."
      },
      // WebPage
      {
        "@type": "WebPage",
        "@id": "https://coderide.ai/pricing#webpage",
        "url": "https://coderide.ai/pricing",
        "name": "CodeRide Pricing - Choose Your AI Coding Plan",
        "description": "Choose the perfect CodeRide plan for your AI coding needs. From free Explorer to lifetime Early Adopter plans.",
        "isPartOf": {
          "@type": "WebSite",
          "name": "CodeRide",
          "url": "https://coderide.ai"
        },
        "about": {
          "@id": "https://coderide.ai/pricing#product"
        },
        "inLanguage": "en-US"
      }
    ]
  };

  return (
    <div className="flex flex-col min-h-screen font-sans">
      <SEO 
        title="CodeRide Pricing - Choose Your AI Coding Plan | Task Management for AI"
        description="Choose the perfect CodeRide plan for your AI coding needs. From free Explorer to lifetime Early Adopter plans. Scale your AI development with persistent context management."
        canonical="https://coderide.ai/pricing"
        keywords={[
          'CodeRide pricing',
          'AI coding plans',
          'AI development subscription',
          'context management pricing',
          'AI assistant plans',
          'developer tools pricing',
          'AI coding subscription',
          'Cursor alternative pricing',
          'AI code editor plans',
          'developer productivity pricing'
        ]}
        entities={[
          'CodeRide',
          'AI',
          'pricing',
          'subscription',
          'developer tools',
          'AI coding',
          'context management'
        ]}
        topics={[
          'AI Development Pricing',
          'Developer Tools Subscription',
          'AI Coding Plans',
          'Software Development Pricing'
        ]}
        structuredData={structuredData}
      />
      <Header />
      
      <main className="flex-1">
        {/* Load critical sections eagerly for immediate visibility */}
        <PricingHero />
        <PricingTiers />
        
        {/* Lazy load remaining sections with invisible fallbacks */}
        <Suspense fallback={<SectionLoader />}>
          <FAQSection />
        </Suspense>
      </main>
      
      <Footer />
    </div>
  );
};

export default Pricing;
